import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGBarChart, PGLineChart, PGPieChart } from '../../components/primitives/PGChart';
import { GROUP, INSURERS, REGIONS } from '../../utils/orgData';
import {
  REP_ROLLUPS,
  PIPELINE_GROUP,
  channelRevenueMix,
  REVENUE_TRENDS,
  MONTHS_SALES,
  fmtRk,
} from '../../utils/opportunityData';
import { PG } from '../../utils/theme';
import OpportunitiesGroupView from './OpportunitiesGroupView';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';
import TickerNumber from '../../components/live/TickerNumber';
import SplitFlap from '../../components/live/SplitFlap';

const {
  FiFileText, FiTarget, FiDollarSign, FiTrendingUp,
  FiAward, FiClock, FiHeart, FiActivity, FiZap,
} = FiIcons;

const TabButton = ({ active, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`h-9 px-4 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] font-bold transition-all ${
      active
        ? 'bg-[#D71921] text-white'
        : 'bg-transparent text-neutral-500 hover:text-white hover:bg-white/5'
    }`}
  >
    {Icon && <Icon className="text-sm" />}
    {children}
  </button>
);

const npsFromScore = (score) => Math.round((score - 6) * 30);

const winRateColor = (v) => (v >= 70 ? PG.emerald : v >= 50 ? PG.amber : PG.red);

const DEAL_EVENTS_SEED = [
  'WON R 3,420 // MAPUTO — T.SITHOLE',
  'LOST — QUOTED_ELSEWHERE // GAUTENG — L.PATEL',
  'WON R 5,890 // CAPE_TOWN — N.KHUMALO',
  'WON R 2,100 // MIDRAND — R.VDBERG',
  'LOST — PRICE // BELLVILLE — A.PRETORIUS',
  'WON R 4,680 // SANDTON — J.MOKOENA',
  'LOST — TIMELINE // HARARE — K.DUBE',
  'WON R 3,340 // LUSAKA — M.BOTHA',
  'WON R 7,220 // PRETORIA_EAST — S.NAIDOO',
  'LOST — STOCK // KHOMAS — C.VISSER',
  'WON R 2,980 // ROODEPOORT — P.OOSTHUIZEN',
  'WON R 5,120 // NAIROBI — T.SITHOLE',
];

export default function SalesSliver() {
  const [view, setView] = useState('sales');
  const s = GROUP.kpis.sales;
  const fin = GROUP.kpis.finance;
  const cx = GROUP.kpis.cx;
  const pg = PIPELINE_GROUP;

  const feed = useLiveFeed();
  const liveRevenue = Math.round(feed.revenueToday * 8.8);
  const liveQuotes = Math.round(feed.jobsDoneToday * 9);

  const insurerRev = [...INSURERS].sort((a, b) => b.revenueShare - a.revenueShare);

  const regionConv = REGIONS.map((r) => ({
    name: r.name,
    val: Math.round(r.branches.reduce((acc, b) => acc + b.sales.quoteConversion, 0) / r.branches.length),
  }));

  const regionNps = REGIONS.map((r) => ({
    name: r.name,
    val: npsFromScore(r.branches.reduce((acc, b) => acc + b.cx.nps, 0) / r.branches.length),
  }));

  const channelMix = useMemo(
    () => channelRevenueMix().map((c) => ({ name: c.name, value: c.revenue })),
    []
  );

  const topReps = useMemo(
    () => [...REP_ROLLUPS].sort((a, b) => b.revenueClosedMTD - a.revenueClosedMTD).slice(0, 10),
    []
  );

  const lostReasonData = pg.lostReasonBreakdown.map((l) => ({ name: l.name, value: l.count }));

  const yoyDelta = useMemo(() => {
    const cur = REVENUE_TRENDS.groupRevenue[REVENUE_TRENDS.groupRevenue.length - 1];
    const prior = REVENUE_TRENDS.groupRevenuePriorYear[REVENUE_TRENDS.groupRevenuePriorYear.length - 1];
    if (!prior) return 0;
    return ((cur - prior) / prior) * 100;
  }, []);

  const yoyTone = yoyDelta >= 0 ? 'ok' : 'alert';
  const yoyLabel = `YoY ${yoyDelta >= 0 ? '+' : ''}${yoyDelta.toFixed(1)}%`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="flex items-center flex-shrink-0 border-b border-white/5 pb-3">
        <div className="inline-flex border border-white/10">
          <TabButton active={view === 'sales'} onClick={() => setView('sales')} icon={FiActivity}>
            Sales_Perf
          </TabButton>
          <TabButton active={view === 'opps'} onClick={() => setView('opps')} icon={FiZap}>
            Opportunities
          </TabButton>
        </div>
        <div className="ml-auto font-mono-tech text-[9px] uppercase tracking-[0.28em] text-neutral-600">
          {view === 'sales' ? 'MONITORING // REVENUE & CONVERSION' : 'MONITORING // PIPELINE & DEALS'}
        </div>
      </div>

      {view === 'opps' ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <OpportunitiesGroupView />
        </div>
      ) : (
        <>
      <div className="grid grid-cols-8 gap-3 flex-shrink-0">
        <KPICard
          title="Quotes_MTD"
          value={s.quotesIssued.toLocaleString()}
          icon={FiFileText}
          accent={PG.white}
          animated="ticker"
          numericValue={liveQuotes}
          tickerDigits={4}
        />
        <KPICard
          title="Conversion"
          value={s.quoteConversion}
          unit="%"
          icon={FiTarget}
          accent={s.quoteConversion >= 60 ? PG.emerald : PG.amber}
          target=">60%"
          sparkValues={feed.throughputPerMin}
        />
        <KPICard
          title="Avg_Job_Value"
          value={`R ${(s.avgJobValue / 1000).toFixed(1)}k`}
          icon={FiDollarSign}
          accent={PG.red}
        />
        <KPICard
          title="Revenue_MTD"
          value={fmtRk(fin.revenueMTD)}
          icon={FiTrendingUp}
          accent={PG.violet}
          animated="ticker"
          numericValue={liveRevenue}
          tickerDigits={9}
          tickerPrefix="R "
        />
        <KPICard
          title="Pipeline"
          value={fmtRk(pg.pipelineValue)}
          icon={FiTrendingUp}
          accent={PG.cyan}
        />
        <KPICard
          title="Win_Rate"
          value={pg.winRate}
          unit="%"
          icon={FiAward}
          accent={pg.winRate >= 70 ? PG.emerald : PG.amber}
          target=">70%"
          animated="splitflap"
          flapText={String(pg.winRate).padStart(4, ' ')}
          flapWidth={4}
        />
        <KPICard
          title="Quote_to_Close"
          value={pg.avgDaysInStage}
          unit="days"
          icon={FiClock}
          accent={pg.avgDaysInStage <= 14 ? PG.emerald : PG.amber}
          target="≤14d"
        />
        <KPICard
          title="Group_NPS"
          value={cx.nps}
          unit="/10"
          icon={FiHeart}
          accent={cx.nps >= 9 ? PG.emerald : PG.amber}
          target="≥9.0"
        />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel
          title="12_Month_Revenue_Trend"
          subtitle="this year vs prior // ZAR"
          right={<StatusPill tone={yoyTone}>{yoyLabel}</StatusPill>}
          className="col-span-8 h-64 flex-shrink-0"
          livePulse
        >
          <div className="h-full p-2">
            <PGLineChart
              categories={MONTHS_SALES}
              series={[
                {
                  name: 'This Year',
                  data: REVENUE_TRENDS.groupRevenue,
                  itemStyle: { color: PG.red },
                  lineStyle: { color: PG.red, width: 2 },
                },
                {
                  name: 'Prior Year',
                  data: REVENUE_TRENDS.groupRevenuePriorYear,
                  itemStyle: { color: PG.neutral500 },
                  lineStyle: { color: PG.neutral500, width: 1, type: 'dashed' },
                  areaStyle: undefined,
                },
              ]}
              yFormatter={(v) => (v / 1e6).toFixed(0) + 'M'}
            />
          </div>
        </Panel>

        <Panel title="Revenue_By_Channel" subtitle="booked revenue share" className="col-span-4 h-64 flex-shrink-0">
          <div className="h-full p-2">
            <PGPieChart data={channelMix} />
          </div>
        </Panel>

        <Panel title="Revenue_By_Insurer" subtitle="% share" className="col-span-6">
          <div className="h-full p-2">
            <PGBarChart
              categories={insurerRev.map((i) => i.name)}
              series={[
                {
                  name: 'Revenue share',
                  data: insurerRev.map((i, idx) => ({
                    value: i.revenueShare,
                    itemStyle: { color: [PG.red, PG.amber, PG.emerald, PG.cyan, PG.violet, PG.white, PG.neutral500][idx % 7] },
                  })),
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel
          title="Top_Rep_Leaderboard"
          subtitle="top 10 by revenue closed MTD"
          right={<StatusPill tone="info">{REP_ROLLUPS.length} reps</StatusPill>}
          className="col-span-6"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            <div
              className="sales-deals-strip mt-2 h-6 border border-white/10 bg-[#0A0A0A] overflow-hidden flex items-center flex-shrink-0"
            >
              <style>{`
                @keyframes pg-deals-scroll {
                  from { transform: translateX(0); }
                  to { transform: translateX(-50%); }
                }
                .pg-deals-track {
                  display: inline-block;
                  animation: pg-deals-scroll 45s linear infinite;
                  white-space: nowrap;
                  will-change: transform;
                }
                .sales-deals-strip:hover .pg-deals-track { animation-play-state: paused; }
                @media (prefers-reduced-motion: reduce) {
                  .pg-deals-track { animation: none !important; }
                }
              `}</style>
              <div className="pg-deals-track font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                {[...DEAL_EVENTS_SEED, ...DEAL_EVENTS_SEED].map((e, i) => (
                  <span key={i} className="px-6">
                    {e.startsWith('WON') ? (
                      <span className="text-[#00FF41]">{e}</span>
                    ) : (
                      <span className="text-[#FFB800]">{e}</span>
                    )}
                    <span className="text-[#1A1A1A] ml-2">·</span>
                  </span>
                ))}
              </div>
            </div>
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                <tr className="border-b border-white/10">
                  <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">#</th>
                  <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Rep</th>
                  <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Branch</th>
                  <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Won_MTD</th>
                  <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Pipeline</th>
                  <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Win%</th>
                </tr>
              </thead>
              <tbody>
                {topReps.map((r, i) => (
                  <tr
                    key={r.id}
                    className="row-clickable border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => { window.location.hash = `#/sliver/sales/branch/${r.branchId}`; }}
                  >
                    <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-500">
                      <SplitFlap text={String(i + 1).padStart(2, '0')} width={2} />
                    </td>
                    <td className="px-3 py-2 font-mono-tech text-[11px] uppercase tracking-wider text-white">{r.name}</td>
                    <td className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400">{r.branchName}</td>
                    <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">{fmtRk(r.revenueClosedMTD)}</td>
                    <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">{fmtRk(r.pipelineOwned)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-doto text-base" style={{ color: winRateColor(r.winRate90d) }}>
                        {r.winRate90d}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Quote_Conversion_By_Region" subtitle="%" className="col-span-4 h-56 flex-shrink-0">
          <div className="h-full p-2">
            <PGBarChart
              categories={regionConv.map((r) => r.name)}
              series={[
                {
                  name: 'Conversion %',
                  data: regionConv.map((r) => ({
                    value: r.val,
                    itemStyle: { color: r.val >= 65 ? PG.emerald : r.val >= 55 ? PG.amber : PG.red },
                  })),
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel title="NPS_By_Region" subtitle="classic NPS (-100 → +100)" className="col-span-4 h-56 flex-shrink-0">
          <div className="h-full p-2">
            <PGBarChart
              categories={regionNps.map((r) => r.name)}
              series={[
                {
                  name: 'NPS',
                  data: regionNps.map((r) => ({
                    value: r.val,
                    itemStyle: { color: r.val >= 70 ? PG.emerald : r.val >= 40 ? PG.amber : PG.red },
                  })),
                },
              ]}
            />
          </div>
        </Panel>

        <Panel title="Lost_Deal_Reasons" subtitle="closed-lost breakdown" className="col-span-4 h-56 flex-shrink-0">
          <div className="h-full p-2">
            <PGPieChart data={lostReasonData} />
          </div>
        </Panel>
      </div>
        </>
      )}
    </motion.div>
  );
}
